import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  Home,
  Car,
  FileText,
  Bell,
  BarChart3,
  User,
  LogOut,
  Shield,
  Info,
  UserCircle2,
} from 'lucide-react';
interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userData, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const navItems = [
    { path: '/', label: t('nav.dashboard'), icon: Home },
    { path: '/vehicles', label: t('nav.vehicles'), icon: Car },
    { path: '/records', label: t('nav.records'), icon: FileText },
    { path: '/reminders', label: t('nav.reminders'), icon: Bell },
    { path: '/analytics', label: t('nav.analytics'), icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="relative z-40 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center gap-3">
                  <img
                    src="/favicon.png"
                    alt={t('common.appName')}
                    className="h-8 w-8 rounded-md"
                  />
                  <span className="sr-only">{t('common.appName')}</span>
                </Link>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isActive
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center">
              {user && (
                <div className="relative" ref={menuRef}>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setMenuOpen((v) => !v)}
                      className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-gray-100 text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      aria-label={t('nav.userMenu', 'User menu')}
                    >
                      <UserCircle2 className="h-5 w-5" />
                    </button>
                    <span className="text-sm text-gray-700">
                      {userData?.displayName || user.email}
                    </span>
                  </div>

                  {menuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-200"
                    >
                      <Link
                        to="/profile"
                        role="menuitem"
                        className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setMenuOpen(false)}
                      >
                        <User className="h-4 w-4 text-gray-500" />
                        {t('nav.profile')}
                      </Link>

                      <a
                        href="/about"
                        target="_blank"
                        rel="noreferrer"
                        role="menuitem"
                        className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setMenuOpen(false)}
                      >
                        <Info className="h-4 w-4 text-gray-500" />
                        {t('auth.brand.aboutLink', 'About project')}
                      </a>

                      {userData?.isAdmin && (
                        <Link
                          to="/administration"
                          role="menuitem"
                          className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMenuOpen(false)}
                        >
                          <Shield className="h-4 w-4 text-gray-500" />
                          {t('nav.administration', 'Administration')}
                        </Link>
                      )}

                      {userData?.isAdmin && (
                        <div className="h-px bg-gray-100" role="separator" />
                      )}

                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <LogOut className="h-4 w-4 text-gray-500" />
                        {t('auth.logout')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
